import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import VouchersApi from '../../api/vouchersApi';
import PlansApi from '../../api/plansApi';
import routePaths from '../../routePaths';
import ConfirmationIconButton from '../common/ConfirmationIconButton';
import { actions as notificationActions } from '../../actions/notificationActions';

const voucherCodeModes = {
  shared: 'Jeden kod',
  unique: 'Kody jednorazowe',
};

const formatDateTime = value => value ? new Date(value).toLocaleString('pl-PL') : '';

const toDateTimeLocalValue = value => {
  const date = value ? new Date(value) : new Date();
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const toIsoDate = value => new Date(value).toISOString();

const isSuccessfulResponse = response => response.ok && (!response.data || response.data.success !== false);

const hasValidManualCodeFormat = code => /^[A-Za-z0-9-]{6,64}$/.test((code || '').trim());

const getStatus = promotion => {
  if (promotion.disabled) return 'Wyłączona';
  const now = new Date();
  if (new Date(promotion.validFrom) > now) return 'Zaplanowana';
  if (new Date(promotion.validTo) < now) return 'Zakończona';
  return 'Aktywna';
};

const getInitialForm = planId => {
  const now = new Date();
  return {
    name: '',
    planId: planId || '',
    validFrom: toDateTimeLocalValue(now),
    validTo: toDateTimeLocalValue(new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000)),
    codeMode: 'shared',
    code: '',
    maxRedemptions: 50,
    codesCount: 50,
  };
};

class VoucherPromotionsPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      promotions: [],
      plans: [],
      loading: false,
      createDialogOpen: false,
      creating: false,
      createError: '',
      createdPromotionId: null,
      generatedCodes: [],
      form: getInitialForm(),
    };

    this.loadData = this.loadData.bind(this);
    this.handleOpenCreateDialog = this.handleOpenCreateDialog.bind(this);
    this.handleCloseCreateDialog = this.handleCloseCreateDialog.bind(this);
    this.handleCreateChange = this.handleCreateChange.bind(this);
    this.handleCreateSubmit = this.handleCreateSubmit.bind(this);
    this.handleDisable = this.handleDisable.bind(this);
  }

  componentDidMount() {
    this.loadData();
  }

  loadData() {
    this.setState({ loading: true });
    return Promise.all([VouchersApi.getPromotions(), PlansApi.getAll()])
      .then(([promotionsResponse, plansResponse]) => {
        if (!promotionsResponse.ok || !plansResponse.ok) {
          this.props.notificationActions.showError('Nie udało się pobrać voucherów');
          return;
        }

        this.setState({
          promotions: promotionsResponse.data || [],
          plans: plansResponse.data || [],
        });
      })
      .finally(() => this.setState({ loading: false }));
  }

  handleOpenCreateDialog() {
    const firstVoucherPlan = this.state.plans.find(plan => plan.voucherOnly);
    this.setState({
      createDialogOpen: true,
      createError: '',
      createdPromotionId: null,
      generatedCodes: [],
      form: getInitialForm(firstVoucherPlan ? firstVoucherPlan.id : ''),
    });
  }

  handleCloseCreateDialog() {
    if (this.state.creating) return;
    this.setState({ createDialogOpen: false });
  }

  handleCreateChange(field, value) {
    this.setState(state => ({
      form: {
        ...state.form,
        [field]: value,
      },
    }));
  }

  validateForm() {
    const { form } = this.state;
    if (!form.name.trim()) return 'Podaj nazwę promocji';
    if (!form.planId) return 'Wybierz plan voucherowy';
    if (!form.validFrom || !form.validTo) return 'Podaj zakres ważności';
    if (new Date(form.validFrom) >= new Date(form.validTo)) return 'Data końca musi być późniejsza niż data początku';
    if (form.codeMode === 'shared' && Number(form.maxRedemptions) <= 0) return 'Podaj liczbę użyć kodu';
    if (form.codeMode === 'shared' && form.code.trim() && !hasValidManualCodeFormat(form.code)) {
      return 'Kod może mieć od 6 do 64 znaków i zawierać tylko litery bez polskich znaków, cyfry oraz myślnik';
    }
    if (form.codeMode === 'unique' && Number(form.codesCount) <= 0) return 'Podaj liczbę generowanych kodów';
    return '';
  }

  handleCreateSubmit() {
    const error = this.validateForm();
    if (error) {
      this.setState({ createError: error });
      return;
    }

    const { form } = this.state;
    const payload = {
      name: form.name,
      planId: form.planId,
      validFrom: toIsoDate(form.validFrom),
      validTo: toIsoDate(form.validTo),
      codeMode: form.codeMode,
      code: form.codeMode === 'shared' ? form.code : null,
      maxRedemptions: form.codeMode === 'shared' ? Number(form.maxRedemptions) : null,
      codesCount: form.codeMode === 'unique' ? Number(form.codesCount) : null,
    };

    this.setState({ creating: true, createError: '' });
    VouchersApi.createPromotion(payload)
      .then(response => {
        if (response.ok && response.data && response.data.success !== false) {
          this.setState({
            createdPromotionId: response.data.id,
            generatedCodes: response.data.codes || [],
          });
          this.props.notificationActions.showSuccess('Utworzono promocję voucherową');
          return this.loadData();
        }

        this.setState({ createError: 'Utworzenie promocji nie powiodło się' });
        this.props.notificationActions.showError('Utworzenie promocji nie powiodło się');
      })
      .finally(() => this.setState({ creating: false }));
  }

  handleDisable(id) {
    return VouchersApi.disablePromotion(id).then(response => {
      if (isSuccessfulResponse(response)) {
        this.props.notificationActions.showSuccess('Promocja została wyłączona');
        return this.loadData();
      }

      this.props.notificationActions.showError('Nie udało się wyłączyć promocji');
      return null;
    });
  }

  renderCreateDialog() {
    const { createDialogOpen, creating, createError, generatedCodes, createdPromotionId, form, plans } = this.state;
    const voucherPlans = plans.filter(plan => plan.voucherOnly && !plan.isDisabled);
    const generatedCodesText = generatedCodes.join('\n');

    return (
      <Dialog open={createDialogOpen} onClose={this.handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Nowa promocja voucherowa</DialogTitle>
        <DialogContent>
          {generatedCodes.length ? (
            <div>
              <Typography variant="subtitle1" gutterBottom>Wygenerowane kody</Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Pełne kody są widoczne tylko teraz. Po zamknięciu okna w szczegółach promocji będzie dostępna wyłącznie końcówka kodu, więc skopiuj kody przed zamknięciem.
              </Typography>
              <TextField
                value={generatedCodesText}
                multiline
                fullWidth
                variant="outlined"
                rows={Math.min(generatedCodes.length, 12)}
                InputProps={{ readOnly: true }}
              />
              <div style={{ marginTop: 16 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigator.clipboard && navigator.clipboard.writeText(generatedCodesText)}
                >
                  Kopiuj kody
                </Button>
              </div>
            </div>
          ) : (
            <div>
              {!voucherPlans.length && (
                <Typography color="error" gutterBottom>
                  Najpierw utwórz plan voucherowy.
                </Typography>
              )}
              <TextField
                label="Nazwa"
                value={form.name}
                onChange={event => this.handleCreateChange('name', event.target.value)}
                fullWidth
                margin="normal"
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Plan voucherowy</InputLabel>
                <Select value={form.planId} onChange={event => this.handleCreateChange('planId', event.target.value)}>
                  {voucherPlans.map(plan => (
                    <MenuItem key={plan.id} value={plan.id}>{plan.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <div style={{ display: 'flex', gap: 16 }}>
                <TextField
                  label="Ważna od"
                  type="datetime-local"
                  value={form.validFrom}
                  onChange={event => this.handleCreateChange('validFrom', event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  margin="normal"
                />
                <TextField
                  label="Ważna do"
                  type="datetime-local"
                  value={form.validTo}
                  onChange={event => this.handleCreateChange('validTo', event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  margin="normal"
                />
              </div>
              <FormControl fullWidth margin="normal">
                <InputLabel>Tryb kodów</InputLabel>
                <Select value={form.codeMode} onChange={event => this.handleCreateChange('codeMode', event.target.value)}>
                  <MenuItem value="shared">Jeden kod z limitem użyć</MenuItem>
                  <MenuItem value="unique">Generowane kody jednorazowe</MenuItem>
                </Select>
              </FormControl>
              {form.codeMode === 'shared' ? (
                <div style={{ display: 'flex', gap: 16 }}>
                  <TextField
                    label="Kod"
                    value={form.code}
                    onChange={event => this.handleCreateChange('code', event.target.value)}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Liczba użyć"
                    type="number"
                    value={form.maxRedemptions}
                    onChange={event => this.handleCreateChange('maxRedemptions', event.target.value)}
                    fullWidth
                    margin="normal"
                  />
                </div>
              ) : (
                <TextField
                  label="Liczba kodów"
                  type="number"
                  value={form.codesCount}
                  onChange={event => this.handleCreateChange('codesCount', event.target.value)}
                  fullWidth
                  margin="normal"
                />
              )}
              {createError && <Typography color="error">{createError}</Typography>}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          {generatedCodes.length && createdPromotionId ? (
            <Button color="primary" onClick={() => this.props.history.push(`${routePaths.voucherPromotionDetails}${createdPromotionId}`)}>
              Przejdź do szczegółów
            </Button>
          ) : (
            <Button color="primary" variant="contained" disabled={creating || !voucherPlans.length} onClick={this.handleCreateSubmit}>
              Utwórz
            </Button>
          )}
          <Button onClick={this.handleCloseCreateDialog} disabled={creating}>Zamknij</Button>
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    const { promotions, loading } = this.state;

    return (
      <div className="page">
        <div style={{ margin: 40 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <Button variant="contained" color="primary" onClick={this.handleOpenCreateDialog}>
              Nowa promocja
            </Button>
          </div>
          {this.renderCreateDialog()}
          <Paper style={{ padding: 16 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nazwa</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Tryb</TableCell>
                  <TableCell>Ważność</TableCell>
                  <TableCell>Użycia</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Akcje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && promotions.map(promotion => (
                  <TableRow
                    key={promotion.id}
                    hover
                    onClick={() => this.props.history.push(`${routePaths.voucherPromotionDetails}${promotion.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <TableCell>{promotion.name}</TableCell>
                    <TableCell>{promotion.planName}</TableCell>
                    <TableCell>{voucherCodeModes[promotion.codeMode] || promotion.codeMode}</TableCell>
                    <TableCell>{formatDateTime(promotion.validFrom)} - {formatDateTime(promotion.validTo)}</TableCell>
                    <TableCell>{promotion.redemptionsCount} / {promotion.maxRedemptions}</TableCell>
                    <TableCell>{getStatus(promotion)}</TableCell>
                    <TableCell align="right" onClick={event => event.stopPropagation()}>
                      {!promotion.disabled && (
                        <ConfirmationIconButton
                          confirmationTitle="Wyłączenie promocji"
                          confirmationText={`Czy na pewno chcesz wyłączyć promocję ${promotion.name}?`}
                          onConfirm={() => this.handleDisable(promotion.id)}
                          onCancel={() => {}}
                          tooltip="Wyłącz promocję"
                          aria-label="Wyłącz promocję"
                        >
                          <DeleteIcon />
                        </ConfirmationIconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!loading && !promotions.length && (
              <Typography style={{ padding: 24, textAlign: 'center' }}>Nie zdefiniowano jeszcze promocji voucherowych</Typography>
            )}
            {loading && <Typography style={{ padding: 24, textAlign: 'center' }}>Ładowanie...</Typography>}
          </Paper>
        </div>
      </div>
    );
  }
}

VoucherPromotionsPage.propTypes = {
  history: PropTypes.object.isRequired,
  notificationActions: PropTypes.object.isRequired,
};

function mapDispatchToProps(dispatch) {
  return {
    notificationActions: bindActionCreators(notificationActions, dispatch),
  };
}

export default connect(null, mapDispatchToProps)(VoucherPromotionsPage);
